import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Close, Refresh, Visibility } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES, ROLE_DISPLAY_NAMES } from '../../constants/roles';
import {
  ASSESSMENT_RESULT_OPTIONS,
  AUTH_NUMBER_PREFIX,
  FORM_REFS,
  STATUS_COLORS,
  STATUS_LABELS
} from '../../constants/instructorAuthorization';
import GetsCm002bApplicationForm, {
  emptyGetsCm002bForm
} from './GetsCm002bApplicationForm';
import GetsCm001cCertificateForm, {
  emptyGetsCm001cCertificate
} from './GetsCm001cCertificateForm';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';
import {
  ATTACHMENT_SECTIONS,
  deleteApplicationAttachment,
  deleteRowAttachment,
  deleteSectionAttachment,
  downloadApplicationAttachment,
  emptyPendingRowFiles,
  emptyPendingSectionFiles,
  getSectionAttachmentsFromDoc,
  rowPendingKey,
  uploadApplicationAttachments,
  uploadPendingRowAttachments,
  uploadRowAttachments,
  uploadSectionAttachments,
  viewApplicationAttachment
} from '../../utils/authorizationAttachments';

const API = '/api/instructor-authorization';

const boardUserFilter = createFilterOptions({
  stringify: (option) =>
    `${option.firstName || ''} ${option.lastName || ''} ${option.email || ''} ${option.role || ''} ${ROLE_DISPLAY_NAMES[option.role] || ''}`
});

const formatBoardUserLabel = (user) => {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
  const roleLabel = ROLE_DISPLAY_NAMES[user.role] || user.role || '';
  return roleLabel ? `${name} (${roleLabel})` : name;
};

const boardAutocompleteWideProps = {
  fullWidth: true,
  ListboxProps: {
    sx: {
      maxHeight: 360,
      '& .MuiAutocomplete-option': {
        whiteSpace: 'nowrap',
        display: 'block',
        overflow: 'visible'
      }
    }
  },
  slotProps: {
    popper: {
      sx: {
        zIndex: (theme) => theme.zIndex.modal + 2,
        width: 'auto !important',
        minWidth: '100%',
        maxWidth: 'min(960px, calc(100vw - 32px))',
        '& .MuiAutocomplete-paper': {
          width: 'max-content',
          minWidth: '100%',
          maxWidth: 'min(960px, calc(100vw - 32px))'
        }
      }
    }
  },
  sx: { width: '100%', minWidth: { xs: 0, sm: 520 } }
};

const emptyForm = (user) =>
  emptyGetsCm002bForm({
    applicantName: user
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : '',
    applicantDeclarationDate: dayjs().format('YYYY-MM-DD')
  });

const emptyBoardSeat = () => ({
  user: null,
  userId: '',
  name: '',
  designation: ''
});

/**
 * @param {object} props
 * @param {boolean} [props.embedded]
 * @param {'full'|'crew'|'mro'} [props.variant]
 */
function InstructorAuthorizationPage({ embedded = false, variant = 'full' } = {}) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [docTab, setDocTab] = useState(0);
  const [form, setForm] = useState(() => emptyForm());
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingSectionFiles, setPendingSectionFiles] = useState(() => emptyPendingSectionFiles());
  const [pendingRowFiles, setPendingRowFiles] = useState(() => emptyPendingRowFiles());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        applicantName: prev.applicantName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
      }));
    }
  }, [user]);

  const [managerDecision, setManagerDecision] = useState({
    decision: 'ACCEPTED',
    remarks: ''
  });

  const [mroDecision, setMroDecision] = useState({
    decision: 'ACCEPTED',
    remarks: '',
    checkedLicences: true,
    checkedTrainings: true,
    checkedExperience: true,
    checkedCompanyApproval: true,
    competencyAssessmentRequired: true
  });

  const [boardCandidates, setBoardCandidates] = useState([]);
  const [boardForm, setBoardForm] = useState({
    member1: emptyBoardSeat(),
    member2: emptyBoardSeat(),
    member3: emptyBoardSeat()
  });

  const [assessmentResults, setAssessmentResults] = useState({
    boardResult: 'SATISFACTORY',
    mroQmRemarks: ''
  });

  const perms = meta?.permissions || {};
  const canApply = perms.canApply;
  const canMroManager = Boolean(perms.canMroManagerReview);
  const canMroQm = perms.canMroQmReview || perms.canMroQmManage;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metaRes, listRes] = await Promise.all([
        axios.get(`${API}/meta`),
        axios.get(API)
      ]);
      setMeta(metaRes.data);
      setRows(listRes.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load instructor authorizations';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadBoardCandidates = useCallback(async () => {
    if (!canMroQm) return;
    try {
      const res = await axios.get(`${API}/board-candidates`);
      setBoardCandidates(res.data || []);
    } catch (err) {
      console.error('Failed to load board candidates', err);
    }
  }, [canMroQm]);

  useEffect(() => {
    if (canMroQm) loadBoardCandidates();
  }, [canMroQm, loadBoardCandidates]);

  const syncAssessmentFromDetail = (doc) => {
    setAssessmentResults({
      boardResult: doc?.competencyAssessment?.boardResult || 'SATISFACTORY',
      mroQmRemarks: doc?.competencyAssessment?.mroQmRemarks || ''
    });
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/${id}`);
      setDocTab(0);
      setDetail(res.data);
      syncAssessmentFromDetail(res.data);
      if (canMroQm) loadBoardCandidates();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load application', 'error');
    }
  };

  const applyBoardUserSelection = (seatKey, value) => {
    if (!value) {
      setBoardForm((b) => ({ ...b, [seatKey]: emptyBoardSeat() }));
      return;
    }
    if (typeof value === 'string') {
      setBoardForm((b) => ({
        ...b,
        [seatKey]: { ...emptyBoardSeat(), name: value }
      }));
      return;
    }
    setBoardForm((b) => ({
      ...b,
      [seatKey]: {
        user: value,
        userId: value._id,
        name: `${value.firstName || ''} ${value.lastName || ''}`.trim(),
        designation:
          b[seatKey]?.designation || ROLE_DISPLAY_NAMES[value.role] || value.role || ''
      }
    }));
  };

  const handleCreate = async () => {
    if (!form.applicantName?.trim()) {
      showToast('Name of the Applicant is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.applicationAttachments;
      const res = await axios.post(API, payload);
      let created = res.data;
      try {
        if (pendingFiles.length) {
          const withAttachments = await uploadApplicationAttachments(API, created._id, pendingFiles);
          if (withAttachments) created = withAttachments;
        }
        for (const section of ATTACHMENT_SECTIONS) {
          const files = pendingSectionFiles[section] || [];
          if (!files.length) continue;
          const withSection = await uploadSectionAttachments(API, created._id, section, files);
          if (withSection) created = withSection;
        }
        if (Object.keys(pendingRowFiles).length) {
          const withRows = await uploadPendingRowAttachments(
            API,
            created._id,
            created,
            pendingRowFiles
          );
          if (withRows) created = withRows;
        }
      } catch (uploadErr) {
        showToast(
          uploadErr.response?.data?.message || 'Draft created, but some attachments failed to upload',
          'warning'
        );
      }
      showToast(`Draft application created (${FORM_REFS.application})`, 'success');
      setCreateOpen(false);
      setForm(emptyForm(user));
      setPendingFiles([]);
      setPendingSectionFiles(emptyPendingSectionFiles());
      setPendingRowFiles(emptyPendingRowFiles());
      await load();
      setDetail(created);
      syncAssessmentFromDetail(created);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create application', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPendingFiles = (files) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPendingSectionFiles = (section, files) => {
    setPendingSectionFiles((prev) => ({
      ...prev,
      [section]: [...(prev[section] || []), ...files]
    }));
  };

  const handleRemovePendingSectionFile = (section, index) => {
    setPendingSectionFiles((prev) => ({
      ...prev,
      [section]: (prev[section] || []).filter((_, i) => i !== index)
    }));
  };

  const handleUploadApplicationAttachment = async (files) => {
    if (!detail?._id || !files?.length) return;
    setSaving(true);
    try {
      const updated = await uploadApplicationAttachments(API, detail._id, files);
      if (updated) {
        setDetail(updated);
        syncAssessmentFromDetail(updated);
      }
      showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApplicationAttachment = async (att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteApplicationAttachment(API, detail._id, att._id);
      setDetail(updated);
      syncAssessmentFromDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSectionAttachment = async (section, files) => {
    if (!detail?._id || !files?.length) return;
    setSaving(true);
    try {
      const updated = await uploadSectionAttachments(API, detail._id, section, files);
      if (updated) {
        setDetail(updated);
        syncAssessmentFromDetail(updated);
      }
      showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSectionAttachment = async (section, att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteSectionAttachment(API, detail._id, section, att._id);
      setDetail(updated);
      syncAssessmentFromDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRowFiles = async (section, index, files, row) => {
    if (!files?.length) return;
    if (detail?._id && row?._id) {
      setSaving(true);
      try {
        const updated = await uploadRowAttachments(API, detail._id, section, row._id, files);
        if (updated) {
          setDetail(updated);
          syncAssessmentFromDetail(updated);
        }
        showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
        await load();
      } catch (err) {
        showToast(err.response?.data?.message || 'Upload failed', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }
    const key = rowPendingKey(section, index);
    setPendingRowFiles((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), ...files]
    }));
  };

  const handleRemovePendingRowFile = (section, index, fileIndex) => {
    const key = rowPendingKey(section, index);
    setPendingRowFiles((prev) => {
      const next = { ...prev };
      next[key] = (next[key] || []).filter((_, i) => i !== fileIndex);
      if (!next[key].length) delete next[key];
      return next;
    });
  };

  const handleRemoveRowIndex = (section, removedIndex) => {
    setPendingRowFiles((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, files]) => {
        const [sec, idxStr] = key.split(':');
        if (sec !== section) {
          next[key] = files;
          return;
        }
        const idx = Number(idxStr);
        if (idx === removedIndex) return;
        const newIdx = idx > removedIndex ? idx - 1 : idx;
        next[rowPendingKey(section, newIdx)] = files;
      });
      return next;
    });
  };

  const handleDeleteRowAttachment = async (section, row, att) => {
    if (!detail?._id || !row?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteRowAttachment(API, detail._id, section, row._id, att._id);
      setDetail(updated);
      syncAssessmentFromDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/${detail._id}`, {
        applicantName: detail.applicantName,
        applicationType: detail.applicationType,
        employeeType: detail.employeeType,
        employeeId: detail.employeeId,
        designation: detail.designation,
        trainTheTrainer: detail.trainTheTrainer,
        caNo: detail.caNo,
        caExpiry: detail.caExpiry,
        requiredScopeOfApproval: detail.requiredScopeOfApproval,
        qualificationDetails: detail.qualificationDetails,
        experienceDetails: detail.experienceDetails,
        trainingDetails: detail.trainingDetails,
        applicantDeclarationDate: detail.applicantDeclarationDate,
        applicantDeclarationPlace: detail.applicantDeclarationPlace,
        companyPolicyAssessment: detail.companyPolicyAssessment
      });
      let saved = res.data;
      if (Object.keys(pendingRowFiles).length) {
        const withRows = await uploadPendingRowAttachments(API, saved._id, saved, pendingRowFiles);
        if (withRows) saved = withRows;
        setPendingRowFiles(emptyPendingRowFiles());
      }
      setDetail(saved);
      showToast('Application saved', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/submit`);
      setDetail(res.data);
      showToast('Submitted to MRO Manager', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Submit failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleManagerDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/mro-manager/decide`, managerDecision);
      setDetail(res.data);
      showToast(
        managerDecision.decision === 'ACCEPTED'
          ? 'Accepted — forwarded to MRO QM'
          : 'Returned to applicant',
        managerDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMroDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/mro-qm/decide`, mroDecision);
      setDetail(res.data);
      showToast(
        mroDecision.decision === 'ACCEPTED' ? 'Accepted — create assessment board next' : 'Returned to applicant',
        mroDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!detail?._id) return;
    if (!boardForm.member1.name?.trim()) {
      showToast('Member 1 is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const members = [
        {
          seatRole: 'MEMBER_1',
          name: boardForm.member1.name,
          userId: boardForm.member1.userId || undefined,
          designation: boardForm.member1.designation || undefined
        }
      ];
      if (boardForm.member2.name?.trim()) {
        members.push({
          seatRole: 'MEMBER_2',
          name: boardForm.member2.name,
          userId: boardForm.member2.userId || undefined,
          designation: boardForm.member2.designation || undefined
        });
      }
      if (boardForm.member3.name?.trim()) {
        members.push({
          seatRole: 'MEMBER_3',
          name: boardForm.member3.name,
          userId: boardForm.member3.userId || undefined,
          designation: boardForm.member3.designation || undefined
        });
      }
      const res = await axios.post(`${API}/${detail._id}/board`, { members });
      setDetail(res.data);
      syncAssessmentFromDetail(res.data);
      showToast('Assessment board created (Head of Board = you / CMM)', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Board creation failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAssessmentComplete = async () => {
    if (!detail?._id) return;
    if (!assessmentResults.boardResult) {
      showToast('Overall board result is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const members = (detail.board?.members || []).map((m) => ({
        seatRole: m.seatRole,
        name: m.name,
        userId: m.userId?._id || m.userId || undefined,
        designation: m.designation || undefined
      }));
      const res = await axios.put(`${API}/${detail._id}/assessment`, {
        markComplete: true,
        boardResult: assessmentResults.boardResult,
        mroQmRemarks: assessmentResults.mroQmRemarks,
        members
      });
      setDetail(res.data);
      syncAssessmentFromDetail(res.data);
      showToast('Assessment marked complete', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to complete assessment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadApplicationPdf = async () => {
    if (!detail?._id) return;
    try {
      await downloadAuthenticatedPdf(
        `${API}/${detail._id}/application/pdf`,
        `GETS-CM-002B_${(detail.applicantName || 'applicant').replace(/\s+/g, '_')}.pdf`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download application PDF', 'error');
    }
  };

  const buildCertificatePayload = (cert, issued) => ({
    issued: Boolean(issued),
    authorizationNo: cert.authorizationNo,
    iaRevisionNo: cert.iaRevisionNo,
    iaRevisionDate: cert.iaRevisionDate || dayjs().format('YYYY-MM-DD'),
    dateOfInitialIa: cert.dateOfInitialIa || dayjs().format('YYYY-MM-DD'),
    validFrom: cert.validFrom,
    validUntil: cert.validUntil,
    dateOfBirth: cert.dateOfBirth,
    licenseNumber: cert.licenseNumber,
    licenseExpiry: cert.licenseExpiry,
    recurrentTrainingValidTill: cert.recurrentTrainingValidTill,
    limitations: cert.limitations,
    scopeRows: cert.scopeRows,
    dgcaApprovalNoDated: cert.dgcaApprovalNoDated,
    dgcaApprovalValidity: cert.dgcaApprovalValidity
  });

  const ensureCertificateDraft = (detailDoc) => {
    const existing = detailDoc?.certificate || {};
    return emptyGetsCm001cCertificate({
      ...existing,
      authorizationNo: existing.authorizationNo || AUTH_NUMBER_PREFIX,
      iaRevisionNo: existing.iaRevisionNo || '00',
      validFrom: existing.validFrom
        ? String(existing.validFrom).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      validUntil: existing.validUntil
        ? String(existing.validUntil).slice(0, 10)
        : dayjs().add(1, 'year').format('YYYY-MM-DD'),
      dateOfInitialIa: existing.dateOfInitialIa
        ? String(existing.dateOfInitialIa).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      iaRevisionDate: existing.iaRevisionDate
        ? String(existing.iaRevisionDate).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      dgcaApprovalValidity: existing.dgcaApprovalValidity
        ? String(existing.dgcaApprovalValidity).slice(0, 10)
        : '',
      dateOfBirth: existing.dateOfBirth ? String(existing.dateOfBirth).slice(0, 10) : '',
      licenseExpiry: existing.licenseExpiry ? String(existing.licenseExpiry).slice(0, 10) : '',
      recurrentTrainingValidTill: existing.recurrentTrainingValidTill
        ? String(existing.recurrentTrainingValidTill).slice(0, 10)
        : '',
      scopeRows:
        Array.isArray(existing.scopeRows) && existing.scopeRows.length
          ? existing.scopeRows
          : undefined
    });
  };

  const handleIssue = async (issued) => {
    if (!detail?._id) return;
    const cert = detail.certificate || ensureCertificateDraft(detail);
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/${detail._id}/issue`,
        buildCertificatePayload(cert, issued)
      );
      setDetail(res.data);
      showToast(
        issued ? `Authorization issued (${FORM_REFS.certificate})` : 'Marked not issued',
        'success'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Issue failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCertificatePdf = async () => {
    if (!detail?._id) return;
    try {
      if (canMroQm && detail.certificate) {
        await axios.put(
          `${API}/${detail._id}/certificate`,
          buildCertificatePayload(detail.certificate, detail.certificate.issued)
        );
      }
      const authNo = (detail.certificate?.authorizationNo || 'GETS-IA').replace(/\s+/g, '_');
      const name = (detail.applicantName || 'authorization').replace(/\s+/g, '_');
      await downloadAuthenticatedPdf(
        `${API}/${detail._id}/certificate/pdf`,
        `GETS-CM-001C_${authNo}_${name}.pdf`
      );
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to download certificate PDF', 'error');
    }
  };

  const editableStatuses = useMemo(() => ['DRAFT', 'RETURNED_TO_APPLICANT'], []);
  const currentUserId = user?._id || user?.id;
  const isApplicantOwner =
    detail &&
    currentUserId &&
    String(detail.applicantUserId?._id || detail.applicantUserId) === String(currentUserId);
  const canEditDetail =
    detail &&
    editableStatuses.includes(detail.status) &&
    (isApplicantOwner ||
      [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role));
  const canManageAttachments =
    Boolean(detail) &&
    (canEditDetail ||
      canMroQm ||
      [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role));

  const canManageAssessment =
    canMroQm &&
    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS', 'ASSESSMENT_COMPLETE'].includes(detail?.status);

  const canIssueCertificate =
    canMroQm &&
    ['ASSESSMENT_COMPLETE', 'ASSESSMENT_IN_PROGRESS', 'BOARD_FORMED'].includes(detail?.status);

  const renderBoardSeatFields = (seatKey, label, required = false) => {
    const seat = boardForm[seatKey];
    return (
      <>
        <Grid item xs={12}>
          <Autocomplete
            {...boardAutocompleteWideProps}
            freeSolo
            options={boardCandidates}
            filterOptions={boardUserFilter}
            groupBy={(option) => ROLE_DISPLAY_NAMES[option.role] || option.role}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : formatBoardUserLabel(option)
            }
            isOptionEqualToValue={(a, b) =>
              a &&
              b &&
              typeof a !== 'string' &&
              typeof b !== 'string' &&
              String(a._id) === String(b._id)
            }
            value={seat.user || seat.name || null}
            onChange={(_, value) => applyBoardUserSelection(seatKey, value)}
            onInputChange={(_, inputValue, reason) => {
              if (reason === 'input') {
                setBoardForm((b) => ({
                  ...b,
                  [seatKey]: { ...emptyBoardSeat(), name: inputValue }
                }));
              }
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const optionLabel =
                typeof option === 'string' ? option : formatBoardUserLabel(option);
              return (
                <li key={key || option._id || optionLabel} {...optionProps}>
                  {optionLabel}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required={required}
                fullWidth
                label={label}
                placeholder="Search board candidates…"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={`${seatKey === 'member1' ? 'Member 1' : seatKey === 'member2' ? 'Member 2' : 'Member 3'} designation`}
            value={seat.designation}
            onChange={(e) =>
              setBoardForm((b) => ({
                ...b,
                [seatKey]: { ...b[seatKey], designation: e.target.value }
              }))
            }
          />
        </Grid>
      </>
    );
  };

  const subtitle =
    variant === 'crew'
      ? 'Apply for issue / extension / renewal of instructor authorization (GETS/CM/002B).'
      : variant === 'mro'
        ? 'Instructor applications; MRO QM review, board, and issue.'
        : `${FORM_REFS.application} → ${FORM_REFS.certificate}`;

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <GetsBrandHeader title="Instructor Authorization" subtitle={subtitle} />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {embedded ? (
          <Box>
            <Typography variant="h6">Instructor Authorization</Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1}>
          <IconButton onClick={load} title="Refresh">
            <Refresh />
          </IconButton>
          {canApply && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setForm(emptyForm(user));
                setPendingFiles([]);
                setPendingSectionFiles(emptyPendingSectionFiles());
                setPendingRowFiles(emptyPendingRowFiles());
                setCreateOpen(true);
              }}
            >
              New application
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Auth No.</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No applications yet</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    {row.applicantName ||
                      `${row.applicantUserId?.firstName || ''} ${row.applicantUserId?.lastName || ''}`.trim() ||
                      '—'}
                  </TableCell>
                  <TableCell>{row.applicationType}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[row.status] || row.status}
                      color={STATUS_COLORS[row.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{row.certificate?.authorizationNo || '—'}</TableCell>
                  <TableCell>
                    {row.updatedAt ? dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(row._id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPendingFiles([]);
          setPendingSectionFiles(emptyPendingSectionFiles());
          setPendingRowFiles(emptyPendingRowFiles());
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>New application — {FORM_REFS.application}</DialogTitle>
        <DialogContent dividers>
          <GetsCm002bApplicationForm
            value={form}
            onChange={setForm}
            pendingFiles={pendingFiles}
            attachmentsSaving={saving}
            onAddAttachmentFiles={handleAddPendingFiles}
            onRemovePendingAttachment={handleRemovePendingFile}
            pendingSectionFiles={pendingSectionFiles}
            onAddSectionFiles={handleAddPendingSectionFiles}
            onRemovePendingSectionFile={handleRemovePendingSectionFile}
            pendingRowFiles={pendingRowFiles}
            onAddRowFiles={handleAddRowFiles}
            onRemovePendingRowFile={handleRemovePendingRowFile}
            onRemoveRowIndex={handleRemoveRowIndex}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setPendingFiles([]);
              setPendingSectionFiles(emptyPendingSectionFiles());
              setPendingRowFiles(emptyPendingRowFiles());
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleCreate}>
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        onClose={() => {
          setDetail(null);
          setDocTab(0);
          setPendingRowFiles(emptyPendingRowFiles());
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <span>Instructor Authorization — documents</span>
          <IconButton
            onClick={() => {
              setDetail(null);
              setDocTab(0);
              setPendingRowFiles(emptyPendingRowFiles());
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={STATUS_LABELS[detail.status] || detail.status}
                  color={STATUS_COLORS[detail.status] || 'default'}
                />
                <Typography variant="body2">{detail.applicationType}</Typography>
              </Stack>

              <Tabs
                value={docTab}
                onChange={(_, v) => setDocTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={`1. Application (${FORM_REFS.application})`} />
                <Tab
                  label={`2. Authorization (${FORM_REFS.certificate})`}
                  disabled={
                    !(
                      (canMroQm || detail.status === 'ISSUED' || detail.status === 'NOT_ISSUED') &&
                      [
                        'ASSESSMENT_COMPLETE',
                        'ASSESSMENT_IN_PROGRESS',
                        'BOARD_FORMED',
                        'ISSUED',
                        'NOT_ISSUED'
                      ].includes(detail.status)
                    )
                  }
                />
              </Tabs>

              {docTab === 0 && (
                <Stack spacing={2}>
                  <GetsCm002bApplicationForm
                    value={detail}
                    onChange={(next) => setDetail((d) => ({ ...d, ...next }))}
                    readOnly={!canEditDetail}
                    attachmentsReadOnly={!canManageAttachments}
                    onDownloadPdf={handleDownloadApplicationPdf}
                    attachments={detail.applicationAttachments || []}
                    attachmentsSaving={saving}
                    onAddAttachmentFiles={
                      canManageAttachments ? handleUploadApplicationAttachment : undefined
                    }
                    onViewAttachment={viewApplicationAttachment}
                    onDownloadAttachment={downloadApplicationAttachment}
                    onDeleteAttachment={
                      canManageAttachments ? handleDeleteApplicationAttachment : undefined
                    }
                    sectionAttachments={getSectionAttachmentsFromDoc(detail)}
                    onAddSectionFiles={
                      canManageAttachments ? handleUploadSectionAttachment : undefined
                    }
                    onDeleteSectionAttachment={
                      canManageAttachments ? handleDeleteSectionAttachment : undefined
                    }
                    pendingRowFiles={pendingRowFiles}
                    onAddRowFiles={canManageAttachments ? handleAddRowFiles : undefined}
                    onRemovePendingRowFile={
                      canManageAttachments ? handleRemovePendingRowFile : undefined
                    }
                    onDeleteRowAttachment={
                      canManageAttachments ? handleDeleteRowAttachment : undefined
                    }
                    onRemoveRowIndex={canManageAttachments ? handleRemoveRowIndex : undefined}
                  />

                  {canEditDetail && (
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" disabled={saving} onClick={handleSaveDraft}>
                        Save
                      </Button>
                      <Button variant="contained" disabled={saving} onClick={handleSubmit}>
                        Submit to MRO Manager
                      </Button>
                    </Stack>
                  )}

                  {canMroManager && detail.status === 'SUBMITTED' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO Manager review</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={managerDecision.decision}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, decision: e.target.value }))
                        }
                      >
                        <MenuItem value="ACCEPTED">Accept (forward to MRO QM)</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={managerDecision.remarks}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, remarks: e.target.value }))
                        }
                      />
                      <Button variant="contained" disabled={saving} onClick={handleManagerDecide}>
                        Submit MRO Manager decision
                      </Button>
                    </>
                  )}

                  {detail.mroManagerCheck?.decision && detail.status !== 'SUBMITTED' && (
                    <Alert
                      severity={
                        detail.mroManagerCheck.decision === 'ACCEPTED' ? 'success' : 'warning'
                      }
                    >
                      MRO Manager: {detail.mroManagerCheck.decision}
                      {detail.mroManagerCheck.remarks
                        ? ` — ${detail.mroManagerCheck.remarks}`
                        : ''}
                    </Alert>
                  )}

                  {canMroQm && detail.status === 'ACCEPTED_BY_MRO_MANAGER' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO QM / CMD check</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={mroDecision.decision}
                        onChange={(e) => setMroDecision((d) => ({ ...d, decision: e.target.value }))}
                      >
                        <MenuItem value="ACCEPTED">Accept</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Application checked for
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedLicences}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedLicences: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Licences"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedTrainings}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedTrainings: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Trainings"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedExperience}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedExperience: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Experience"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedCompanyApproval}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedCompanyApproval: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Company Approval"
                        />
                      </FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={mroDecision.competencyAssessmentRequired}
                            onChange={(e) =>
                              setMroDecision((d) => ({
                                ...d,
                                competencyAssessmentRequired: e.target.checked
                              }))
                            }
                          />
                        }
                        label={`Competency assessment required (attach ${FORM_REFS.competencyAttach})`}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={mroDecision.remarks}
                        onChange={(e) => setMroDecision((d) => ({ ...d, remarks: e.target.value }))}
                      />
                      <Button variant="contained" disabled={saving} onClick={handleMroDecide}>
                        Submit MRO QM decision
                      </Button>
                    </>
                  )}

                  {canMroQm && detail.status === 'ACCEPTED_BY_MRO_QM' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">Create assessment board (MRO QM)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Head of Board = you (CMM). Member 1 is required; Member 2 and Member 3 are optional.
                        Select from board candidates or type a name.
                      </Typography>
                      <Alert severity="info" sx={{ py: 0 }}>
                        Head of Board (CMM):{' '}
                        {`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'You'}
                      </Alert>
                      <Grid container spacing={2}>
                        {renderBoardSeatFields(
                          'member1',
                          'Member 1 — select user or type name',
                          true
                        )}
                        {renderBoardSeatFields(
                          'member2',
                          'Member 2 (optional) — select user or type name'
                        )}
                        {renderBoardSeatFields(
                          'member3',
                          'Member 3 (optional) — select user or type name'
                        )}
                      </Grid>
                      <Button variant="contained" disabled={saving} onClick={handleCreateBoard}>
                        Create board
                      </Button>
                    </>
                  )}

                  {detail.board?.members?.length > 0 && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2">Board</Typography>
                      {detail.board.members.map((m, idx) => (
                        <Typography key={idx} variant="body2">
                          {m.seatRole}: {m.name || m.userId?.firstName || '—'}
                          {m.designation ? ` (${m.designation})` : ''}
                        </Typography>
                      ))}
                    </>
                  )}

                  {canManageAssessment &&
                    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS'].includes(detail.status) && (
                      <>
                        <Divider />
                        <Typography variant="subtitle1">Competency assessment results</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Attach relevant assessment form {FORM_REFS.competencyAttach} as applicable.
                          Overall board result is SATISFACTORY / UNSATISFACTORY.
                        </Typography>
                        {(detail.board?.members || []).length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                              Assessment board
                            </Typography>
                            {(detail.board.members || []).map((m, idx) => (
                              <Typography key={idx} variant="body2">
                                {m.seatRole}: {m.name || m.userId?.firstName || '—'}
                                {m.designation ? ` — ${m.designation}` : ''}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              fullWidth
                              label="Overall board result"
                              value={assessmentResults.boardResult}
                              onChange={(e) =>
                                setAssessmentResults((r) => ({ ...r, boardResult: e.target.value }))
                              }
                            >
                              {ASSESSMENT_RESULT_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Compliance Monitoring Manager Remarks"
                              value={assessmentResults.mroQmRemarks}
                              onChange={(e) =>
                                setAssessmentResults((r) => ({ ...r, mroQmRemarks: e.target.value }))
                              }
                            />
                          </Grid>
                        </Grid>
                        <Button
                          variant="contained"
                          disabled={saving}
                          onClick={handleMarkAssessmentComplete}
                        >
                          Mark assessment complete
                        </Button>
                      </>
                    )}
                </Stack>
              )}

              {docTab === 1 && (
                <Stack spacing={2}>
                  {detail.status === 'ISSUED' && detail.certificate?.issued && (
                    <Alert severity="success">
                      Issued {detail.certificate.authorizationNo} ·{' '}
                      {detail.certificate.validFrom
                        ? dayjs(detail.certificate.validFrom).format('DD-MM-YYYY')
                        : '?'}{' '}
                      →{' '}
                      {detail.certificate.validUntil
                        ? dayjs(detail.certificate.validUntil).format('DD-MM-YYYY')
                        : '?'}
                    </Alert>
                  )}
                  <GetsCm001cCertificateForm
                    application={detail}
                    value={ensureCertificateDraft(detail)}
                    onChange={(next) =>
                      setDetail((d) => ({
                        ...d,
                        certificate: { ...(d.certificate || {}), ...next }
                      }))
                    }
                    readOnly={!canIssueCertificate}
                    saving={saving}
                    onIssue={canIssueCertificate ? handleIssue : undefined}
                    onDownloadPdf={handleDownloadCertificatePdf}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default InstructorAuthorizationPage;
